import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'

export function useDebouncedValue<T>(value: T, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedValue(value), delay)
    return () => window.clearTimeout(timeoutId)
  }, [delay, value])

  return debouncedValue
}

export function useStationOperationsList<T>(
  key: string,
  load: () => Promise<T[]>,
  matches: (item: T) => boolean,
  page: number,
  limit: number,
) {
  const query = useQuery(['station-operations', key], load, {
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  })
  const allItems = query.data ?? []
  const filtered = allItems.filter(matches)
  return {
    ...query,
    allItems,
    items: filtered.slice((page - 1) * limit, page * limit),
    total: filtered.length,
  }
}

export function useStationOperationsPage<T, Q extends object>(
  key: string,
  load: (query: Q & { page: number; limit: number }) => Promise<{ data: T[]; total: number }>,
  query: Q,
  page: number,
  limit: number,
) {
  const result = useQuery(
    ['station-operations', key, query, page, limit],
    () => load({ ...query, page, limit }),
    {
      keepPreviousData: true,
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      retry: false,
    },
  )

  return {
    ...result,
    items: result.data?.data ?? [],
    total: result.data?.total ?? 0,
  }
}
